/**
 * Single source of truth for admin navigation.
 *
 * Sidebar.tsx (work surfaces) and Layout.tsx (account dropdown) both render from
 * this file. Adding a page = adding one entry here.
 *
 * Access is the AND of three things:
 *   1. capability  — by tier only (capabilitiesForRole); the same for every company role
 *   2. anyFeature  — at least one of the listed plan features (omit = no plan gate)
 *   3. env         — build environment, for internal-only tools
 */
import {
  LayoutDashboard,
  Calculator,
  Package,
  PackageCheck,
  ShoppingBag,
  Activity,
  MapPin,
  Zap,
  Layers,
  ClipboardList,
  Ticket,
  FileText,
  Truck,
  Globe,
  Clock,
  Mail,
  CalendarClock,
  Bug,
  Building2,
  Users,
  Tag,
  CreditCard,
  Settings,
  Shield,
  Bell,
  BellRing,
  MessageSquare,
  User,
  Lock,
  Headset,
  Share2,
  TrendingUp,
  Link2,
  Star,
  Gauge,
  BarChart3,
  ScrollText,
  Plug,
  Boxes,
  Store,
  DollarSign,
  Warehouse,
  Undo2,
  Printer,
  PackageOpen,
  ClipboardCheck,
  History,
  Smartphone,
  Megaphone,
  Percent,
  PackageSearch,
  type LucideIcon,
  PackageX,
} from 'lucide-react';
import { roleTier } from './roles';
import type { ActionKey } from './actions';

/* ─── Roles ──────────────────────────────────────────────────────────────── */

export type Role =
  | 'super_admin'
  | 'company_admin'
  | 'company_user'
  | 'facility_user'
  // Legacy code for facility_user. Still arrives in tokens issued before the
  // rename, so it stays mapped rather than falling through to no capabilities.
  | 'warehouse_user'
  | 'vertical'
  /**
   * SubAdmin: the admin areas a company admin ticks for the SubAdmin role on
   * Roles & Permissions. Every admin capability, so the areas CAN show; the
   * role's menus and actions decide which actually do.
   */
  | 'sub_admin';

/* ─── Capabilities ───────────────────────────────────────────────────────── */

export type Capability =
  | 'shipping.view'
  | 'tracking.view'
  | 'catalog.manage'
  | 'tickets.view'
  | 'reports.view'
  | 'integrations.manage'
  | 'diagnostics.view'
  | 'team.manage'
  | 'company.manage'
  | 'billing.view'
  | 'account.self'
  /**
   * Sees the company Dashboard. Split out from `shipping.view` because a member
   * with a review queue does not get a dashboard at all - the two are now
   * different answers to "what is my landing page", and one capability could
   * not express both.
   */
  | 'dashboard.view'
  | 'platform.admin';

/**
 * What a signed-in company user's app can show at all. The SAME for every
 * company role - Admin, SubAdmin, Member, Facility User, Vertical, custom:
 * which of these pages a person actually gets is their role's menus
 * (`hasMenuPermission`), and admin areas can only be held by Admin and
 * SubAdmin. Nothing here depends on which role it is.
 */
const TENANT_CAPABILITIES: Capability[] = [
  'shipping.view',
  'dashboard.view',
  'tracking.view',
  'catalog.manage',
  'tickets.view',
  'reports.view',
  'integrations.manage',
  'diagnostics.view',
  'team.manage',
  'company.manage',
  'billing.view',
  'account.self',
];

/**
 * Platform-level only. super_admin operates the platform, it does not belong to
 * a tenant - so it deliberately does NOT get the tenant capabilities. Tenant
 * records are managed through Tenants → Companies Management instead.
 */
const PLATFORM_CAPABILITIES: Capability[] = ['account.self', 'platform.admin'];

/** By tier only (config/roles.ts): the platform, or a company. */
export const capabilitiesForRole = (role?: string): Capability[] =>
  !role ? [] : roleTier(role) === 'super_admin' ? PLATFORM_CAPABILITIES : TENANT_CAPABILITIES;

/* ─── Plan feature codes ─────────────────────────────────────────────────── */

/**
 * Canonical feature codes. Keep in sync with PlanFeature.feature_code and with
 * the API-side catalog. New codes go here first, not straight into the DB.
 */
export const FEATURE_CODES = [
  'rates',
  'shipment',
  'tracing',
  'order_integrations',
  // `auto_batch` was retired Aug 2026 - it is granted by `shipment`.
  'reports',
  'diagnostics',
  'tracking_mocks',
] as const;

export type FeatureCode = (typeof FEATURE_CODES)[number];

/* ─── Nav model ──────────────────────────────────────────────────────────── */

export type NavShell = 'tenant' | 'platform';

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  /** Required capability. */
  capability: Capability;
  /** Passes if the plan has ANY of these. Omit for no plan gate. */
  anyFeature?: FeatureCode[];
  /** Restrict to non-production builds (internal tooling). */
  internalOnly?: boolean;
  /**
   * Temporarily switched off everywhere: not in the sidebar, and its route
   * refuses like any other page the role lacks. Kept in the list so the id
   * still resolves for NavGuard and the menu-parity check. Remove the flag to
   * bring the page back.
   */
  hidden?: boolean;
  /**
   * Render greyed with an upgrade hint instead of hiding when plan-locked.
   * Only honoured for users who can act on it (`billing.view`); everyone else
   * simply does not see out-of-plan items.
   */
  showWhenLocked?: boolean;
  /**
   * Extra path prefixes that count as "on this page" for the active highlight -
   * detail pages that live outside the item's own path. `path` and
   * `path + '/...'` always match; list only what is not under it.
   */
  match?: string[];
  /** Column heading in the group's flyout panel. Entries without one share an untitled column. */
  section?: string;
  /**
   * The role menu that grants this page, when several pages share one role
   * menu (every WMS page is granted by `wms`). Defaults to `id`. Roles,
   * the API and the parity check only ever see this menu id.
   */
  menu?: string;
  /**
   * A role action the page also needs (WMS Receive needs `wms.receive`). The
   * entry hides without it wherever the caller passes `hasAction`.
   */
  action?: ActionKey;
}

/** The role menu id that grants this page (see NavItem.menu). */
export const menuOf = (item: NavItem): string => item.menu ?? item.id;

/**
 * One sidebar entry standing for several pages, drawn as tabs on each of them.
 *
 * NOT a permission. A role is still granted the pages inside, by their own ids,
 * and the route guards and the API still check those ids. The hub only decides
 * how the pages are drawn: it shows when any of its tabs would show, and opens
 * the first one that does. Every tab keeps its own URL.
 */
export interface NavHub {
  id: string;
  label: string;
  icon: LucideIcon;
  tabs: NavItem[];
  /** Column heading in the group's flyout panel (see NavItem.section). */
  section?: string;
}

export type NavEntry = NavItem | NavHub;

export const isHub = (entry: NavEntry): entry is NavHub => 'tabs' in entry;

/** The pages an entry opens: itself, or a hub's tabs. */
const pagesOf = (entry: NavEntry): NavItem[] => (isHub(entry) ? entry.tabs : [entry]);

export interface NavGroup {
  id: string;
  /**
   * The group's name on the rail and its flyout panel. Omit it for a group of
   * one entry, which then shows as that entry.
   */
  label?: string;
  /** Rail icon. A labelled group needs one; a one-entry group uses its entry's. */
  icon?: LucideIcon;
  /** Shown at the bottom of the rail, apart from the work groups (Settings). */
  pinned?: boolean;
  shell: NavShell;
  items: NavEntry[];
}

/* ─── Sidebar: tenant workspace ──────────────────────────────────────────── */

/*
 * Grouped by the job being done. Moving an item between groups never changes
 * who may see it: capability and plan gate are per item, and `anyFeature` must
 * match the API's SYSTEM_MENUS.requiredFeatures for the same id.
 */
export const TENANT_GROUPS: NavGroup[] = [
  {
    // Opens a flyout like every other group. It used to be a hub drawn as tabs
    // in the top bar; the top bar now carries the page title instead.
    //
    // Order is landing order: a role without the Dashboard (a Vertical) lands
    // after login on the first of these it does have (see LandingRoute).
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    shell: 'tenant',
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/', icon: LayoutDashboard, capability: 'dashboard.view' },
      // The built-in Vertical role has it by default, anyone with assigned
      // SKUs is given it, and any role can be given it. Always your own sales.
      // Ahead of the queue so it is a Vertical's home after login.
      { id: 'my-sales', label: 'My Sales', path: '/my-sales', icon: BarChart3, capability: 'shipping.view', anyFeature: ['tracing', 'reports'], showWhenLocked: true, hidden: true },
      // Live: orders with your SKUs not yet delivered. Same people as My Sales.
      { id: 'my-orders', label: 'My Orders', path: '/my-orders', icon: ShoppingBag, capability: 'shipping.view', anyFeature: ['shipment', 'tracing'], showWhenLocked: true, hidden: true },
      // Its own path. It used to share the Dashboard's `/`, which lit up both
      // entries and made the queue unreachable for anyone who also has a
      // Dashboard.
      //
      // A role menu like any other: the built-in Vertical role has it by
      // default, admins always, and any role can be given it. What is IN the
      // queue is still whatever User tags assign that person.
      { id: 'my-queue', label: 'My Review Queue', path: '/my-queue', icon: ClipboardList, capability: 'shipping.view', anyFeature: ['shipment', 'tracing'], showWhenLocked: true, match: ['/review'] },
    ],
  },
  {
    id: 'shipping',
    label: 'Shipping',
    icon: Truck,
    shell: 'tenant',
    items: [
      { id: 'quotation', label: 'Get Quotation', section: 'Orders', path: '/quotation', icon: Calculator, capability: 'shipping.view', anyFeature: ['rates'], showWhenLocked: true },
      { id: 'orders', label: 'Orders History', section: 'Orders', path: '/external-orders', icon: Package, capability: 'shipping.view', anyFeature: ['shipment', 'tracing'], showWhenLocked: true },
      { id: 'returns', label: 'Returns (RMA)', section: 'Orders', path: '/returns', icon: Undo2, capability: 'shipping.view', anyFeature: ['shipment', 'tracing'], showWhenLocked: true },
      { id: 'track-history', label: 'Tracking History', section: 'Tracking', path: '/track-history', icon: Activity, capability: 'tracking.view', anyFeature: ['tracing'], showWhenLocked: true, match: ['/shipment'] },
      { id: 'facility-shipments', label: 'Facility Shipments', section: 'Facilities', path: '/facility-shipments', icon: PackageCheck, capability: 'tracking.view', anyFeature: ['shipment', 'tracing'], showWhenLocked: true },
      { id: 'facilities', label: 'Facilities', section: 'Facilities', path: '/settings/facilities', icon: Building2, capability: 'shipping.view', showWhenLocked: true, match: ['/settings/warehouses'] },
      // Hidden for now - set `hidden: false` (or drop it) to restore.
      { id: 'expediting', label: 'Internal Expediting', section: 'Tracking', path: '/internal-expediting', icon: Zap, capability: 'shipping.view', anyFeature: ['tracing'], showWhenLocked: true, hidden: true },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    icon: Boxes,
    shell: 'tenant',
    items: [
      { id: 'products', label: 'Product List', section: 'Products', path: '/products', icon: Layers, capability: 'shipping.view', anyFeature: ['shipment', 'tracing'], showWhenLocked: true },
      { id: 'tags', label: 'Tag Management', section: 'Products', path: '/tags', icon: Tag, capability: 'shipping.view', anyFeature: ['shipment', 'tracing'], showWhenLocked: true },
      { id: 'repricing', label: 'SKU Repricing', section: 'Pricing', path: '/repricing', icon: TrendingUp, capability: 'shipping.view', anyFeature: ['rates', 'shipment'], showWhenLocked: true },
      { id: 'competitor-prices', label: 'Competitor Prices', section: 'Pricing', path: '/competitor-prices', icon: Link2, capability: 'shipping.view', anyFeature: ['rates', 'shipment'], showWhenLocked: true },
      { id: 'sku-margins', label: 'SKU Margins', section: 'Pricing', path: '/sku-margins', icon: Percent, capability: 'shipping.view', anyFeature: ['rates', 'shipment'], showWhenLocked: true },
      { id: 'purchase-orders', label: 'Purchase Orders', section: 'Purchasing & Packaging', path: '/purchase-orders', icon: FileText, capability: 'shipping.view', showWhenLocked: true },
      { id: 'reorder', label: 'Reorder Runway', section: 'Purchasing & Packaging', path: '/reorder', icon: PackageSearch, capability: 'shipping.view', showWhenLocked: true },
      { id: 'vendors', label: 'Vendors', section: 'Purchasing & Packaging', path: '/vendors', icon: Store, capability: 'shipping.view', showWhenLocked: true },
      { id: 'inventory', label: 'Inventory', section: 'Products', path: '/inventory', icon: Layers, capability: 'shipping.view', showWhenLocked: true },
      { id: 'price-files', label: 'Price Files', section: 'Pricing', path: '/price-files', icon: DollarSign, capability: 'shipping.view', showWhenLocked: true },
      { id: 'boxes', label: 'Boxes & Skids', section: 'Purchasing & Packaging', path: '/settings/boxes', icon: Package, capability: 'shipping.view', anyFeature: ['rates', 'shipment'], showWhenLocked: true },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: BarChart3,
    shell: 'tenant',
    items: [
      { id: 'sales-analytics', label: 'Vertical Sales and Analytics', section: 'Sales', path: '/sales-analytics', icon: TrendingUp, capability: 'reports.view', anyFeature: ['tracing', 'reports'], showWhenLocked: true },
      { id: 'product-reviews', label: 'Product Reviews', section: 'Reviews', path: '/product-reviews', icon: Star, capability: 'shipping.view', anyFeature: ['shipment', 'tracing'], showWhenLocked: true },
      { id: 'listing-suggestions', label: 'Listing Suggestions', section: 'Reviews', path: '/listing-suggestions', icon: MessageSquare, capability: 'shipping.view', anyFeature: ['shipment', 'tracing'], showWhenLocked: true },
      { id: 'facility-scoreboard', label: 'Facility Scoreboard', section: 'Facilities', path: '/facility-scoreboard', icon: Gauge, capability: 'tracking.view', anyFeature: ['shipment', 'tracing'], showWhenLocked: true },
      { id: 'transit', label: 'Transit Locations', section: 'Tracking', path: '/transit-info', icon: MapPin, capability: 'shipping.view', anyFeature: ['tracing'], showWhenLocked: true },
      { id: 'tracking-events', label: 'Tracking Events', section: 'Tracking', path: '/tracking-events', icon: Activity, capability: 'shipping.view', anyFeature: ['tracing'], showWhenLocked: true },
      { id: 'billing-report', label: 'Billing Report', section: 'Billing', path: '/billing-report', icon: FileText, capability: 'reports.view', anyFeature: ['tracing', 'reports'], showWhenLocked: true },
      { id: 'google-ads', label: 'Google Ads', section: 'Advertising', path: '/google-ads', icon: Megaphone, capability: 'reports.view', anyFeature: ['reports'], showWhenLocked: true },
      { id: 'amazon-ads', label: 'Amazon Ads', section: 'Advertising', path: '/amazon-ads', icon: Megaphone, capability: 'reports.view', anyFeature: ['reports'], showWhenLocked: true },
      { id: 'ebay-ads', label: 'eBay Ads', section: 'Advertising', path: '/ebay-ads', icon: Megaphone, capability: 'reports.view', anyFeature: ['reports'], showWhenLocked: true },
    ],
  },
  {
    // Near the end on purpose: tickets and logs are where you go when
    // something went wrong, not where daily work starts.
    // Named for what it opens: "Support" at the top right is Contact Support,
    // a different page, and two things called Support confused people.
    id: 'support',
    label: 'Tickets',
    icon: Ticket,
    shell: 'tenant',
    items: [
      { id: 'tickets', label: 'Internal Tickets', path: '/internal-tickets', icon: Ticket, capability: 'tickets.view', anyFeature: ['tracing'], showWhenLocked: true },
    ],
  },
  {
    // Every log page, listed on its own in the Logs panel.
    id: 'logs',
    label: 'Logs',
    icon: ScrollText,
    shell: 'tenant',
    items: [
      { id: 'auto-batch-logs', label: 'Auto Batch Logs', path: '/settings/auto-batch-logs', icon: Clock, capability: 'diagnostics.view', anyFeature: ['shipment'], showWhenLocked: true },
      // Keeps its shipping.view gate, so a member who had it before still
      // gets it here.
      { id: 'packaging-logs', label: 'Packaging Logs', path: '/settings/packaging-logs', icon: ClipboardList, capability: 'shipping.view', anyFeature: ['rates', 'shipment'], showWhenLocked: true },
      { id: 'smtp-logs', label: 'Email Logs', path: '/settings/smtp-logs', icon: Mail, capability: 'diagnostics.view', anyFeature: ['shipment', 'tracing', 'diagnostics'], showWhenLocked: true },
      { id: 'sms-logs', label: 'SMS Logs', path: '/settings/sms-logs', icon: MessageSquare, capability: 'diagnostics.view', anyFeature: ['shipment', 'tracing', 'diagnostics'], showWhenLocked: true },
      // A raw carrier API log, so it sits with the other logs - but it keeps
      // `reports.view`, so members who had it still do.
      { id: 'rate-requests', label: 'Third-Party API Logs', path: '/settings/rate-requests', icon: Activity, capability: 'reports.view', anyFeature: ['rates', 'shipment', 'tracing', 'reports'], showWhenLocked: true },
      { id: 'cron-schedules', label: 'Cron Schedules', path: '/settings/cron-schedules', icon: CalendarClock, capability: 'diagnostics.view', anyFeature: ['shipment', 'tracing', 'diagnostics'], showWhenLocked: true },
      // Admin tool, available in every environment. `diagnostics.view` keeps it
      // to company admins; the plan gate matches the API catalog.
      { id: 'tracking-mocks', label: 'Tracking Mocks', path: '/settings/tracking-mocks', icon: Bug, capability: 'diagnostics.view', anyFeature: ['shipment', 'tracing', 'diagnostics'] },
    ],
  },
  {
    // The warehouse mobile app's setup and supervision, below Logs. Each page
    // is its own entry in the flyout, but they are all granted by the one
    // role menu `wms` (see NavItem.menu), so roles need no new permissions.
    // Its sibling role menu `wms-mobile` (sign in to the app) has no page.
    // Today first: it is where a role whose first page is the WMS lands.
    id: 'warehouse',
    label: 'WMS',
    icon: Warehouse,
    shell: 'tenant',
    items: [
      { id: 'wms-today', menu: 'wms', label: 'Today', section: 'Operations', path: '/wms/today', icon: Gauge, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-receive', menu: 'wms', label: 'Receive', section: 'Operations', path: '/wms/receive', icon: PackageOpen, capability: 'shipping.view', showWhenLocked: true, action: 'wms.receive' },
      { id: 'wms-receipts', menu: 'wms', label: 'Receipts', section: 'Operations', path: '/wms/receipts', icon: ClipboardCheck, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-returns', menu: 'wms', label: 'Returns', section: 'Operations', path: '/wms/returns', icon: Undo2, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-shipping', menu: 'wms', label: 'Shipping', section: 'Operations', path: '/wms/shipping', icon: Truck, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-shipped-unpicked', menu: 'wms', label: 'Shipped Unpicked', section: 'Operations', path: '/wms/shipped-unpicked', icon: PackageX, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-inventory', menu: 'wms', label: 'Inventory', section: 'Stock', path: '/wms/inventory', icon: Layers, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-pickups', menu: 'wms', label: 'Picking Records', section: 'Stock', path: '/wms/pickups', icon: ClipboardList, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-containers', menu: 'wms', label: 'Containers', section: 'Stock', path: '/wms/containers', icon: Package, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-activity', menu: 'wms', label: 'Activity', section: 'Stock', path: '/wms/activity', icon: History, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-facilities', menu: 'wms', label: 'Facilities', section: 'Setup', path: '/wms/facilities', icon: Building2, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-locations', menu: 'wms', label: 'Locations', section: 'Setup', path: '/wms/locations', icon: MapPin, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-printers', menu: 'wms', label: 'Printers', section: 'Setup', path: '/wms/printers', icon: Printer, capability: 'shipping.view', showWhenLocked: true },
      { id: 'wms-devices', menu: 'wms', label: 'Devices', section: 'Setup', path: '/wms/devices', icon: Smartphone, capability: 'shipping.view', showWhenLocked: true },
    ],
  },
  {
    // Carrier accounts and store connections: set up once, pinned to the
    // bottom of the rail with Settings.
    id: 'connections',
    label: 'Connect',
    icon: Plug,
    pinned: true,
    shell: 'tenant',
    items: [
      { id: 'carriers', label: 'Carrier Integrations', path: '/settings/carriers', icon: Truck, capability: 'integrations.manage', anyFeature: ['rates', 'shipment', 'tracing'], showWhenLocked: true },
      { id: 'order-integrations', label: 'Integrated Systems', path: '/settings/integrations', icon: Globe, capability: 'integrations.manage', anyFeature: ['rates', 'shipment', 'tracing', 'order_integrations'], showWhenLocked: true },
    ],
  },
  {
    // Company setup, done once and rarely revisited: pinned to the bottom of
    // the rail instead of sitting with the daily work, and out of the account
    // menu, which is left with the user's own profile.
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    pinned: true,
    shell: 'tenant',
    items: [
      { id: 'company-settings', label: 'Company Settings', section: 'Company', path: '/settings/company', icon: Building2, capability: 'company.manage' },
      { id: 'review-questions', label: 'Review Questions', section: 'Company', path: '/settings/review-questions', icon: ClipboardList, capability: 'company.manage' },
      { id: 'team', label: 'Team Members', section: 'Company', path: '/settings/team', icon: Users, capability: 'team.manage' },
      { id: 'roles', label: 'Roles & Permissions', section: 'Company', path: '/settings/roles', icon: Shield, capability: 'team.manage' },
      { id: 'email-templates', label: 'Email Templates', section: 'Messaging', path: '/settings/email-templates', icon: FileText, capability: 'company.manage', anyFeature: ['shipment', 'rates', 'tracing'], showWhenLocked: true },
      { id: 'sms-templates', label: 'SMS Templates', section: 'Messaging', path: '/settings/sms-templates', icon: MessageSquare, capability: 'company.manage', anyFeature: ['shipment', 'rates', 'tracing'], showWhenLocked: true },
      { id: 'subscription', label: 'Subscription & Plan', section: 'Billing', path: '/settings/subscription', icon: CreditCard, capability: 'billing.view' },
    ],
  },
];

/* ─── Sidebar: platform console (super_admin) ────────────────────────────── */

export const PLATFORM_GROUPS: NavGroup[] = [
  {
    id: 'tenants',
    label: 'Tenants',
    icon: Building2,
    shell: 'platform',
    items: [
      { id: 'admin-companies', label: 'Companies Management', path: '/admin/companies', icon: Building2, capability: 'platform.admin' },
      { id: 'admin-users', label: 'Company Users', path: '/admin/users', icon: Shield, capability: 'platform.admin' },
      { id: 'admin-carriers', label: 'Carriers Management', path: '/admin/carriers', icon: Truck, capability: 'platform.admin' },
      { id: 'admin-tracking-sources', label: 'Tracking Sources', path: '/admin/tracking-sources', icon: Share2, capability: 'platform.admin' },
    ],
  },
  {
    id: 'plans-billing',
    label: 'Plans & Billing',
    icon: CreditCard,
    shell: 'platform',
    items: [
      { id: 'admin-plans', label: 'Subscription Plans', path: '/admin/subscription-plans', icon: Layers, capability: 'platform.admin' },
      { id: 'admin-billing-history', label: 'Subscription History', path: '/admin/billing-history', icon: FileText, capability: 'platform.admin' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    icon: Settings,
    shell: 'platform',
    items: [
      { id: 'admin-notifications', label: 'System Notifications', path: '/admin/notifications', icon: Bell, capability: 'platform.admin' },
      { id: 'admin-notification-rules', label: 'Exception Tracking Rules', path: '/admin/notification-rules', icon: BellRing, capability: 'platform.admin' },
      { id: 'admin-email-templates', label: 'System Email Templates', path: '/admin/email-templates', icon: FileText, capability: 'platform.admin' },
      { id: 'admin-sms-templates', label: 'System SMS Templates', path: '/admin/sms-templates', icon: MessageSquare, capability: 'platform.admin' },
    ],
  },
  {
    id: 'observability',
    label: 'Observability',
    icon: Activity,
    shell: 'platform',
    items: [
      { id: 'admin-system-logs', label: 'System Logs', path: '/admin/system-logs', icon: FileText, capability: 'platform.admin' },
      // Third-Party API Report is intentionally absent. RateRequestService is built on
      // TenantContext, which cannot resolve for super_admin (no company) — the page
      // returns 500. Restore this entry once a platform-scoped query exists.
      { id: 'admin-smtp-logs', label: 'System Email Logs', path: '/admin/smtp-logs', icon: Mail, capability: 'platform.admin' },
      { id: 'admin-sms-logs', label: 'System SMS Logs', path: '/admin/sms-logs', icon: MessageSquare, capability: 'platform.admin' },
      { id: 'admin-cron-schedules', label: 'System Cron Schedules', path: '/admin/cron-schedules', icon: CalendarClock, capability: 'platform.admin' },
    ],
  },
];

/* ─── Account dropdown (top right) ───────────────────────────────────────── */

export interface AccountSection {
  id: string;
  label?: string;
  items: NavItem[];
}

export const ACCOUNT_SECTIONS: AccountSection[] = [
  {
    id: 'account',
    label: 'Account',
    items: [
      { id: 'profile', label: 'Edit Profile', path: '/profile', icon: User, capability: 'account.self' },
      { id: 'password', label: 'Change Password', path: '/profile?tab=password', icon: Lock, capability: 'account.self' },
      { id: 'support', label: 'Contact Support', path: '/support', icon: Headset, capability: 'shipping.view' },
    ],
  },
  {
    id: 'platform',
    label: 'Platform',
    items: [
      { id: 'system-settings', label: 'System Settings', path: '/admin/system-settings', icon: Settings, capability: 'platform.admin' },
      { id: 'payment-config', label: 'Payment Configuration', path: '/admin/settings/payment', icon: CreditCard, capability: 'platform.admin' },
      // Tracking Sources lives in the platform sidebar (Tenants) - not repeated here.
    ],
  },
];

/* ─── Access resolution ──────────────────────────────────────────────────── */

export type NavVisibility = 'visible' | 'locked' | 'hidden';

export interface AccessContext {
  can: (capability: Capability) => boolean;
  hasFeature: (code: string) => boolean;
  hasMenuPermission?: (menuId: string) => boolean;
  /** Role actions, for entries that also need one (NavItem.action). Omit to skip that check. */
  hasAction?: (action: ActionKey) => boolean;
  /** Company is exempt from plan checks (noPlanNeed). */
  planExempt?: boolean;
}

const isProduction = () => import.meta.env.MODE === 'production';

/**
 * `visible` — render normally.
 * `locked`  — capability is fine but the plan does not include it; render greyed
 *             with an upgrade link (only when showWhenLocked).
 * `hidden`  — do not render.
 */
export const resolveVisibility = (item: NavItem, ctx: AccessContext): NavVisibility => {
  if (item.hidden) return 'hidden';
  if (item.internalOnly && isProduction()) return 'hidden';
  if (!ctx.can(item.capability)) return 'hidden';

  // Upper Layer Check: Subscription Plan Feature Allowance.
  //
  // Staging commented this out on the grounds that nothing read the result.
  // That is not true here: this branch DOES return 'locked' below, so the gate
  // is live and an out-of-plan item greys out with an upgrade link (or hides,
  // when it is not showWhenLocked). Kept enabled deliberately.
  const planAllowed =
    !item.anyFeature ||
    item.anyFeature.length === 0 ||
    ctx.planExempt ||
    item.anyFeature.some((f) => ctx.hasFeature(f));

  if (!planAllowed) {
    // The lock links to Subscription & Plan. Only show it to someone who can
    // open that page; for everyone else an out-of-plan item is just absent.
    const canOpenBilling = ctx.hasMenuPermission ? ctx.hasMenuPermission('subscription') : ctx.can('billing.view');
    return item.showWhenLocked && canOpenBilling ? 'locked' : 'hidden';
  }

  // Self-account actions (profile, change password) are available to any user with account.self capability
  if (item.capability === 'account.self') {
    return 'visible';
  }

  // Lower Layer Check: Role Menu Permission
  if (ctx.hasMenuPermission && !ctx.hasMenuPermission(menuOf(item))) {
    return 'hidden';
  }

  if (item.action && ctx.hasAction && !ctx.hasAction(item.action)) {
    return 'hidden';
  }

  return 'visible';
};

/**
 * Whether `pathname` is on this item's page, including its detail pages.
 * `/` only matches itself - otherwise the Dashboard would light up everywhere.
 */
export const isItemActive = (item: NavItem, pathname: string): boolean => {
  const under = (base: string) =>
    base === '/' ? pathname === '/' : pathname === base || pathname.startsWith(`${base}/`);
  return under(item.path) || (item.match ?? []).some(under);
};

/**
 * The first page this user may actually open, in sidebar order - where to send
 * someone whose role does not include the page they asked for. `null` when the
 * role opens nothing at all; callers fall back to the profile page.
 */
export const firstVisibleItem = (groups: NavGroup[], ctx: AccessContext): NavItem | null => {
  for (const group of groups) {
    for (const entry of group.items) {
      for (const item of pagesOf(entry)) {
        if (resolveVisibility(item, ctx) === 'visible') return item;
      }
    }
  }
  return null;
};

export const firstVisiblePath = (groups: NavGroup[], ctx: AccessContext): string | null =>
  firstVisibleItem(groups, ctx)?.path ?? null;

/**
 * Every page in the navigation - sidebar entries, hub tabs and the account
 * dropdown - by id. Route guards look pages up here, so a page inside a hub is
 * found exactly like one with its own sidebar entry.
 */
export const allNavItems = (): NavItem[] => [
  ...TENANT_GROUPS.flatMap((group) => group.items.flatMap(pagesOf)),
  ...PLATFORM_GROUPS.flatMap((group) => group.items.flatMap(pagesOf)),
  ...ACCOUNT_SECTIONS.flatMap((section) => section.items),
];

/** The hub with this id, from either sidebar. */
export const findHub = (id: string): NavHub | undefined =>
  [...TENANT_GROUPS, ...PLATFORM_GROUPS]
    .flatMap((group) => group.items)
    .find((entry): entry is NavHub => isHub(entry) && entry.id === id);

/** A hub's tabs this user sees, open or plan-locked, in order. */
export const resolveHubTabs = (hub: NavHub, ctx: AccessContext) =>
  hub.tabs
    .map((item) => ({ item, visibility: resolveVisibility(item, ctx) }))
    .filter((tab) => tab.visibility !== 'hidden');

export interface ResolvedEntry {
  entry: NavEntry;
  visibility: NavVisibility;
  /** Where the entry opens: its own page, or a hub's first open tab. */
  path: string;
  /** Every page that lights the entry up (see isItemActive). */
  pages: NavItem[];
}

/**
 * A hub is as visible as its best tab: visible when any tab opens, locked when
 * the plan locks every tab the role has, hidden when the role has none.
 */
const resolveEntry = (entry: NavEntry, ctx: AccessContext): ResolvedEntry => {
  if (!isHub(entry)) {
    return { entry, visibility: resolveVisibility(entry, ctx), path: entry.path, pages: [entry] };
  }
  const tabs = resolveHubTabs(entry, ctx);
  const open = tabs.find((tab) => tab.visibility === 'visible');
  return {
    entry,
    visibility: open ? 'visible' : tabs.length > 0 ? 'locked' : 'hidden',
    path: open?.item.path ?? entry.tabs[0].path,
    pages: entry.tabs,
  };
};

/** Groups with at least one renderable entry, entries pre-resolved. */
export const resolveGroups = (groups: NavGroup[], ctx: AccessContext) =>
  groups
    .map((group) => ({
      ...group,
      items: group.items
        .map((entry) => resolveEntry(entry, ctx))
        .filter((resolved) => resolved.visibility !== 'hidden'),
    }))
    .filter((group) => group.items.length > 0);

export const resolveSections = (sections: AccountSection[], ctx: AccessContext) =>
  sections
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => ({ item, visibility: resolveVisibility(item, ctx) }))
        .filter((entry) => entry.visibility !== 'hidden'),
    }))
    .filter((section) => section.items.length > 0);
